import { AcumaticaRequestOptions } from './../acumatica.service';
import { AcumaticaService } from '../acumatica.service';
import { AcumaticaEntity, AcumaticaEntityService } from './acumatica-entity';
import { Acumatica } from '../acumatica-field-decorator';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { PreAllocationDetail } from './Shipment.service';

@Injectable({
    providedIn: 'root'
})
export class InventorySummaryInquiryService extends AcumaticaEntityService {
    constructor(acumatica: AcumaticaService) {
        super(acumatica, 'InventorySummaryInquiry', InventorySummaryInquiry);
    }

    get(inventoryID: string): Observable<InventorySummaryInquiry>;
    get(inventoryID: string, options: AcumaticaRequestOptions): Observable<InventorySummaryInquiry>;
    get(inquiry: InventorySummaryInquiry): Observable<InventorySummaryInquiry>;
    get(inquiry: InventorySummaryInquiry, options: AcumaticaRequestOptions): Observable<InventorySummaryInquiry>;
    get(inquiry: InventorySummaryInquiry | string, options: AcumaticaRequestOptions = {}): Observable<InventorySummaryInquiry> {
        if (typeof (inquiry) == 'string') inquiry = new InventorySummaryInquiry({ InventoryID: inquiry });
        options.noCache = true; // never cache inventory data

        return super.putEntity(inquiry, {
            ...options,
            ...{ expand: "Results" }
        });
    }

    /** get Inventory Summary Detail records for the given inventory ID and warehouse ID, and optionally filtered to a given Lot/Serial Nbr */
    getWhseInventory(warehouseID: string, inventoryID: string): Observable<InventorySummaryDetail[]>;
    getWhseInventory(warehouseID: string, inventoryID: string, expandByLotSerialNbr: boolean): Observable<InventorySummaryDetail[]>;
    getWhseInventory(warehouseID: string, inventoryID: string, lotSerialNbr: string): Observable<InventorySummaryDetail[]>;
    getWhseInventory(warehouseID: string, inventoryID: string, p3?: string | boolean, p4?: boolean): Observable<InventorySummaryDetail[]> {
        // argument-shifting
        let expandByLotSerialNbr: boolean = (typeof p3 == 'boolean') ? p3 : (p4 || false);
        let lotSerialNbr: string | undefined = (typeof p3 == 'string') ? p3 : undefined;
        if (lotSerialNbr !== undefined) expandByLotSerialNbr = true;

        return new Observable<InventorySummaryDetail[]>(subscriber => {
            let inquiry = {
                InventoryID: inventoryID,
                WarehouseID: warehouseID,
                ExpandByLotSerialNbr: expandByLotSerialNbr
            };

            this.get(new InventorySummaryInquiry(inquiry), { filter: undefined, loader: null }).subscribe(result => {
                if (lotSerialNbr !== undefined) result.Results = result.Results.filter(row => row.LotSerialNbr == lotSerialNbr);
                subscriber.next(result.Results);
                subscriber.complete();
            });
        })
    }

    /** return total quantity available of a given item for a specified warehouse */
    getWhseQtyAvailable(inventoryID: string, warehouseID: string): Observable<number> {
        return new Observable<number>(subscriber => {
            this.getWhseInventory(warehouseID, inventoryID).subscribe(results => {
                subscriber.next(results.map(row => row.QtyAvailable).sum());
                subscriber.complete();
            })
        });
    }
}

export class InventorySummaryDetail extends AcumaticaEntity {
    private _locationID: string | null = null;
    @Acumatica() get LocationID(): string | null { return this._locationID; }
    set LocationID(locationID: string | null) {
        if (this.OriginalLocationID == null) this.OriginalLocationID = locationID;
        this._locationID = locationID;
    }
    OriginalLocationID: string | null = null;

    @Acumatica({
        reader: (value: string) => value == '' ? null : value,
        writer: (value: string | null) => value || ''
    }) LotSerialNbr: string | null = null;
    @Acumatica() LotHash: string | null = null;

    private _qtyAvailable: number = 0;
    @Acumatica()
    get QtyAvailable(): number { return this._qtyAvailable + (this.PreAllocated?.Quantity || 0); }
    set QtyAvailable(value: number) { this._qtyAvailable = value; }
    private _qtyAvailableForShipment: number = 0;
    @Acumatica()
    get QtyAvailableForShipment(): number { return this._qtyAvailableForShipment + (this.PreAllocated?.Quantity || 0); }
    set QtyAvailableForShipment(value: number) { this._qtyAvailableForShipment = value; }
    @Acumatica() QtyNotAvailable: number = 0;
    @Acumatica() QtyOnHand: number = 0;
    @Acumatica() WarehouseID: string | null = null;
    @Acumatica() BaseUOM!: string;

    PreAllocated?: PreAllocationDetail;
}

export class InventorySummaryInquiry extends AcumaticaEntity {
    @Acumatica() InventoryID!: string;
    @Acumatica() LocationID!: string | null;
    @Acumatica() WarehouseID!: string | null;
    @Acumatica() ExpandByLotSerialNbr: boolean = true;

    @Acumatica(InventorySummaryDetail) Results!: InventorySummaryDetail[];

    constructor(inquiry?: Partial<InventorySummaryInquiry>) {
        super();
        Object.assign(this, inquiry);
    }
}